import {
  ColumnDataType,
  GetOutputSchemasParams,
  Provider,
  QueryAndOutputs,
  TableSchemasList,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentConditionCodeParams,
  WorkflowComponentOutputTablesParams,
  WorkflowComponentOutputType,
  WorkflowComponentParameterMode,
  WorkflowComponentParamType
} from '../types'

import dedent from 'ts-dedent'
import {
  ELSE_OUTPUT_NAME,
  formatValue,
  getValueTypeFromSchema,
  IF_OUTPUT_NAME,
  nop
} from '../utils'
import { WorkflowComponent } from './common/workflowcomponent'

const operators = new Map([
  ['equal to', 'Is equal to...'],
  ['not equal', 'Is not equal to...'],
  ['greater than', 'Is greater than...'],
  ['equal or greater than', 'Is greater than or equal to...'],
  ['less than', 'Is less than...'],
  ['equal or less than', 'Is less than or equal to...']
])

const operatorSymbol = {
  'equal to': '=',
  'not equal': '!=',
  'greater than': '>',
  'equal or greater than': '>=',
  'less than': '<',
  'equal or less than': '<='
}

const aggregations = ['MIN', 'MAX', 'AVG', 'SUM', 'COUNT']

class IfElse extends WorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.ifelse',
        group: 'Control',
        title: 'Conditional Split',
        description: `
          This component evaluates a condition on input data and splits the workflow into "If" and "Else" outputs, enabling conditional processing.
        `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'type',
          type: WorkflowComponentParamType.Selection,
          title: '',
          options: ['simpleCondition', 'customExpression'],
          default: 'simpleCondition',
          mode: WorkflowComponentParameterMode.Toggle
        },
        {
          name: 'column',
          type: WorkflowComponentParamType.Column,
          title: 'Column',
          showIf: [{ parameter: 'type', value: 'simpleCondition' }],
          dataType: [ColumnDataType.Number],
          parent: 'source'
        },
        {
          name: 'aggregation',
          type: WorkflowComponentParamType.Selection,
          title: 'Aggregation method',
          showIf: [{ parameter: 'type', value: 'simpleCondition' }],
          options: aggregations
        },
        {
          name: 'operator',
          type: WorkflowComponentParamType.Selection,
          title: 'Operator',
          options: Array.from(operators.keys()),
          optionsText: Array.from(operators.values()),
          showIf: [{ parameter: 'type', value: 'simpleCondition' }]
        },
        {
          name: 'value',
          type: WorkflowComponentParamType.String,
          title: 'Value',
          showIf: [{ parameter: 'type', value: 'simpleCondition' }]
        },
        {
          name: 'customexpression',
          type: WorkflowComponentParamType.StringSql,
          title: '',
          placeholder: 'AVG(population) > 3000',
          showIf: [{ parameter: 'type', value: 'customExpression' }]
        }
      ],
      [
        {
          name: IF_OUTPUT_NAME,
          description: 'If table',
          type: WorkflowComponentOutputType.Table,
          title: IF_OUTPUT_NAME
        },
        {
          name: ELSE_OUTPUT_NAME,
          description: 'Else table',
          type: WorkflowComponentOutputType.Table,
          title: ELSE_OUTPUT_NAME
        }
      ]
    )
  }

  async getFullCodeSnippet({
    params
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    return {
      query: '',
      outputs: {}
    }
  }

  async getCode(_: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    return {
      query: '',
      outputs: {}
    }
  }

  getOutputTables({
    params
  }: WorkflowComponentOutputTablesParams): Record<string, string> {
    return {
      [IF_OUTPUT_NAME]: params.source as string,
      [ELSE_OUTPUT_NAME]: params.source as string
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  _getOutputSchemas({
    params,
    schemas,
    connection
  }: GetOutputSchemasParams): TableSchemasList {
    const columns = this._getSchemaForTablename(
      params.source as string,
      schemas,
      connection
    )
    return [
      {
        fqn: params.source as string,
        schema: columns
      }
    ]
  }

  getConditionCode({
    workflow,
    params,
    componentName,
    connection,
    ifQuery,
    elseQuery,
    codeType,
    schemas
  }: WorkflowComponentConditionCodeParams): string {
    if (codeType === WorkflowCodeType.DryRun) {
      return ''
    }
    let value = params.value
    let valueType = ColumnDataType.String
    if (!workflow.isExpression(componentName, 'value')) {
      if (schemas) {
        const schema = schemas.find((s) => s.componentName === componentName)
          ?.schemas?.[0]?.schema
        if (schema) {
          valueType = getValueTypeFromSchema(params.column as string, schema)
          value = formatValue(
            params.value as string,
            connection.type,
            valueType
          )
        }
      }
    }

    let select = ''
    if (params.type === 'customExpression') {
      select = (params.customexpression as string) ?? ''
    } else {
      const tablevalue = `${params.aggregation as string}(${
        params.column as string
      })`
      const operator = operatorSymbol[params.operator as string]
      select = `${tablevalue} ${operator} ${value}`
    }

    const expression = dedent`
      SELECT ${select}
      FROM ${connection.quoteName(params.source as string)}
    `

    ifQuery ||= nop(connection.type)
    elseQuery ||= nop(connection.type)

    switch (connection.type) {
      case Provider.BigQuery:
      case Provider.Redshift:
      case Provider.Postgres:
        return dedent`
          IF
          (
            ${expression}
          )
          THEN
            ${ifQuery}
          ELSE
            ${elseQuery}
          END IF;`
      case Provider.Snowflake:
        return dedent`
          BEGIN
            IF
            ((
              ${expression}
            ))
            THEN
              ${ifQuery}
            ELSE
              ${elseQuery}
            END IF;
          END;`
      case Provider.DatabricksRest:
        return dedent`
          expression_value = spark.sql(f"""
            ${expression}
          """).collect()[0][0]
          if expression_value:
            ${ifQuery}
          else:
            ${elseQuery}`
      default:
        throw new Error(`Unsupported connection type: ${connection.type}`)
    }
  }
  supportedProviders(): Provider[] {
    return [
      Provider.BigQuery,
      Provider.Postgres,
      Provider.Redshift,
      Provider.Snowflake,
      Provider.DatabricksRest
    ]
  }
}

export { IfElse }
